'use client';

import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { buildTelas } from '@/app/ajuda/helpers/ajudaConteudo';
import { telaDaRota } from '@/app/ajuda/helpers/ajudaDaRota';
import { ajudaHref } from '@/app/ajuda/helpers/ajudaNavegacao';
import { ACTIVE_TAB_PARAM } from '@/domain/common/hooks/useActiveTabParam';
import { useUrlQueryState } from '@/domain/common/hooks/useUrlQueryState';

/**
 * O "?" do cabeçalho: o capítulo DESTA tela no manual, a um clique de onde a dúvida
 * aparece.
 *
 * A Ajuda só servia a quem lembrava de sair da tela e ir procurá-la, e quem está travado
 * numa tela não faz esse caminho. Agora a tela leva o manual junto: o botão abre a Ajuda
 * já na tela aberta, com o que ela faz e as dúvidas dela.
 *
 * Ele mora no `PageTitle` do produto, e não em cada página, porque toda rota usa esse
 * cabeçalho: assim nenhuma tela nasce sem o "?" por esquecimento, e uma rota que o manual
 * não descreve (a própria Ajuda, uma rota de detalhe) simplesmente não ganha botão.
 */
export function AjudaDaTela() {
  const { t } = useTranslation('common');
  const pathname = usePathname();
  const { getParam } = useUrlQueryState();

  const telas = useMemo(() => buildTelas(t), [t]);
  const abaPedida = getParam(ACTIVE_TAB_PARAM);
  const tela = telaDaRota(telas, pathname ?? '', abaPedida === '' ? null : abaPedida);

  if (tela === null) {
    return null;
  }

  const rotulo = t('ajuda.daTela.abrir', { tela: tela.nome });

  return (
    <ControlledTooltip content={rotulo}>
      <LinkButton
        href={ajudaHref({ tela: tela.id })}
        variant="text"
        tone="neutral"
        size="sm"
        aria-label={rotulo}
        className="shrink-0 rounded-full text-foreground-subtle hover:text-brand-emphasis"
      >
        <MaterialSymbol icon="help" size={20} />
      </LinkButton>
    </ControlledTooltip>
  );
}
